import api from "./client";

export type VoiceStatus =
  | "connected"
  | "sync_pending"
  | "not_configured"
  | "disabled"
  | "sync_error";

export type VoiceTonePreset =
  | "warm_professional"
  | "calm_concise"
  | "upbeat_friendly"
  | "premium_polished"
  | "custom";

export interface OwnerVoiceSettings {
  ok: true;
  assistant_summary: string;
  tone_preset: VoiceTonePreset;
  tone_custom_instructions: string | null;
  tone_custom_instructions_max_length: number;
  chat_greeting: string | null;
  chat_greeting_max_length: number;
  enabled: boolean;
  status: VoiceStatus;
  status_label: string;
  phone_number: string | null;
  phone_status: string;
  assistant_greeting: string | null;
  assistant_greeting_max_length: number;
  voice?: {
    enabled: boolean;
    status: VoiceStatus;
    status_label: string;
    phone_number: string | null;
    phone_status: string;
    assistant_greeting: string | null;
    assistant_greeting_max_length: number;
    can_sync: boolean;
    safe_error: string | null;
  };
  can_save: boolean;
  can_preview_assistant: boolean;
  can_sync: boolean;
  safe_error: string | null;
}

export interface PatchVoiceSettingsRequest {
  tone_preset: VoiceTonePreset;
  chat_greeting?: string | null;
  assistant_greeting?: string | null;
  tone_custom_instructions?: string | null;
}

export interface PatchVoiceSettingsResponse {
  ok: true;
  status: VoiceStatus;
  status_label: string;
  safe_message: string;
}

export interface SyncVoiceSettingsResponse {
  ok: true;
  status: VoiceStatus;
  status_label: string;
  safe_message: string;
}

export interface VoiceAssistantPreviewSection {
  id: "business_identity" | "assistant_style";
  title: string;
  collapsed_by_default: boolean;
  content: Record<string, string | null>;
}

export interface VoiceAssistantPreviewResponse {
  ok: true;
  sections: VoiceAssistantPreviewSection[];
}

export async function getVoiceSettings(
  businessId: string
): Promise<OwnerVoiceSettings> {
  const res = await api.get<OwnerVoiceSettings>(
    `/portal/businesses/${businessId}/ai-assistant/settings`
  );
  return res.data;
}

export async function patchVoiceSettings(
  businessId: string,
  payload: PatchVoiceSettingsRequest
): Promise<PatchVoiceSettingsResponse> {
  const res = await api.patch<PatchVoiceSettingsResponse>(
    `/portal/businesses/${businessId}/ai-assistant/settings`,
    payload
  );
  return res.data;
}

export async function previewVoiceAssistant(
  businessId: string
): Promise<VoiceAssistantPreviewResponse> {
  const res = await api.post<VoiceAssistantPreviewResponse>(
    `/portal/businesses/${businessId}/ai-assistant/preview`,
    {}
  );
  return res.data;
}

export async function syncVoiceAssistant(
  businessId: string
): Promise<SyncVoiceSettingsResponse> {
  const res = await api.post<SyncVoiceSettingsResponse>(
    `/portal/businesses/${businessId}/voice/vapi/sync`,
    {}
  );
  return res.data;
}
